// src/oura/aeo.ts
//
// The New Scan flow's job store — the live list of assessments this session
// started, rendered as rows inside the Businesses table.
//
// This file used to BE the API: it simulated detect, prescreen and the whole
// worker with `setInterval`, so nothing on the screen ever touched the backend.
// It is now a thin state container over the real client in `@service/aeo`
// (POST /api/v1/assessments → poll GET /api/v1/assessments/{id}). All wire
// shapes, auth and the fixture fallback live there — see that file's header for
// why live calls are still gated behind VITE_ENABLE_OURA_LIVE_API.
//
// Survives a reload: `rehydrateScanJobs()` re-seeds the store from
// GET /api/v1/assessments (domain now optional — ourteam-backend#3793) and
// resumes polling anything still in flight, so a refresh no longer loses a
// running scan. Drives the processing toast in
// src/containers/oura-processing-toast/.

import { useSyncExternalStore } from "react";
import { catchErrorLog } from "@util/sentry";
import {
    cancelAssessment,
    createAssessment,
    detect,
    getAssessment,
    listAllAssessments,
    type AeoAssessment,
    type AeoDetectedBusiness,

    type AeoAssessmentStage,
    type AeoAssessmentEta,
} from "@service/aeo";

export type { AeoDetectedBusiness } from "@service/aeo";

export interface NewScanInput {
    domain: string;
    business_name: string;
    category: string;
    anchors: { primary: string[]; city: string };
    services: string[];
    /** Who asked for this scan (#3509) — metadata only; it changes nothing about the run. */
    requested_by?: string;
}

/**
 * `untracked` is NOT a backend status. It is local-only, and it means "this
 * browser gave up watching; the run may well still be going on the server".
 *
 * It exists because marking a still-running job `failed` after 30 minutes was a
 * lie the UI told, and an expensive one: it read as "the scan stopped", which
 * led to deleting the business and trying to start a replacement — neither of
 * which does anything while the server job is genuinely still in flight.
 */
/**
 * `cancelled` IS a backend status (ourteam-backend#3982), unlike `untracked`
 * above. Someone deliberately stopped this run, so it is styled as a neutral
 * outcome and never as a failure — and it keeps whatever partial results the
 * run had produced before it stopped.
 */
export type ScanJobStatus =
    | "queued" | "running" | "partial" | "complete" | "failed" | "untracked" | "cancelled";

export interface ScanJob {
    assessment_id: string;
    /**
     * Carried from the create request, NOT read back from the poll — the BE's
     * `to_status_dict()` omits `domain`, `business_name` and `category`
     * entirely. Requested in the BE follow-up; until then this local copy is
     * the only source for the row's label.
     */
    domain: string;
    business_name: string;
    category: string;
    status: ScanJobStatus;
    stage: string;
    stages_complete: number;
    stages_total: number;
    /** The pipeline in order, when the backend sends it (#3572). Absent on an
     *  older backend — the row then shows the step counter it always did. */
    stages?: AeoAssessmentStage[] | null;
    /** Roughly how much longer, or null when no honest number exists. */
    eta?: AeoAssessmentEta | null;
    estimated_cost_usd: number;
    /** e.g. "ENGINE_STUBBED" on `partial`, or a failure code on `failed`. */
    error_code?: string;
    /**
     * The request that started this run, kept so Retry can re-submit it verbatim.
     * Absent on a job rehydrated after a reload — the BE's status payload carries
     * domain/name/category but not anchors/services, so a rehydrated failure can
     * be dismissed but not retried in place (the toast hides Retry in that case
     * rather than firing an incomplete request).
     */
    input?: NewScanInput;
}

/** How often to ask the BE how a run is going. Real runs take 5–15 minutes. */
const POLL_INTERVAL_MS = 5_000;
/** Stop polling a run that never reaches a terminal state, so a stuck job can't
 *  poll forever in a tab left open overnight. */
const POLL_TIMEOUT_MS = 30 * 60_000;
/** Consecutive poll failures tolerated before the row is marked failed. */
const MAX_POLL_ERRORS = 3;

// Statuses that stop the poll loop. `untracked` is here because we stop
// WATCHING, not because the run stopped — which is exactly why it needs its own
// value rather than being folded into `failed`.
const TERMINAL: ScanJobStatus[] = ["partial", "complete", "failed", "untracked", "cancelled"];

/**
 * The terminal states this browser writes itself, rather than reading off the
 * wire. Both can be contradicted by a poll response that was already in flight
 * when they landed — see the staleness guard in `poll()`.
 */
const LOCALLY_SETTLED: ScanJobStatus[] = ["cancelled", "untracked"];

/**
 * Crawl the domain and suggest the business details for the Details step.
 * Never throws — an unreachable site yields empty fields + `confidence: "low"`
 * and staff fill the form in by hand.
 */
export function detectBusiness(domain: string): Promise<AeoDetectedBusiness> {
    return detect(domain);
}

// ---------------------------------------------------------------------------
// Job store

let jobs: ScanJob[] = [];
const listeners = new Set<() => void>();

function emit() {
    jobs = [...jobs];
    listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

/** Live list of this session's scan jobs (newest first). */
export function useScanJobs(): ScanJob[] {
    return useSyncExternalStore(subscribe, () => jobs);
}

/** Drop one job from the store (toast dismiss / remove a settled row). */
export function removeScanJob(id: string): void {
    jobs = jobs.filter((j) => j.assessment_id !== id);
    emit();
}

/** Drop every settled job, leaving in-flight ones alone (toast "clear"). */
export function clearSettledScanJobs(): void {
    jobs = jobs.filter((j) => !TERMINAL.includes(j.status));
    emit();
}

export type CancelOutcome = "cancelled" | "already_finished" | "error";

/**
 * Stop a running scan (ourteam-backend#3982).
 *
 * Reports which of three things happened so the caller can say the right thing:
 * a cancel that landed, a run that had already finished (a 409 — the click lost
 * a race with the last poll, which is not an error and must not be shown as
 * one), or a genuine failure worth a retry.
 *
 * The store is patched only on success. On `already_finished` the row is left
 * alone deliberately: the next poll tick carries the real outcome, and painting
 * it "Cancelled" would claim credit for stopping something that had already
 * finished on its own.
 */
export async function cancelScanJob(id: string): Promise<CancelOutcome> {
    const job = jobs.find((j) => j.assessment_id === id);
    if (!job || TERMINAL.includes(job.status)) return "already_finished";

    let result: Awaited<ReturnType<typeof cancelAssessment>>;
    try {
        result = await cancelAssessment(id);
    } catch (err) {
        // The service layer already swallows axios errors into a result, so
        // reaching here means something unexpected — log it rather than let a
        // rejection surface nowhere (callers fire this with `void`).
        catchErrorLog(`[oura] cancel failed for ${id}: ${err}`);
        return "error";
    }

    if (result.ok) {
        // Clear any stale error_code: a run cancelled while it happened to be
        // carrying one would otherwise render the old failure note under a
        // "Cancelled" label.
        patch(id, { status: "cancelled", stage: "Cancelled", error_code: undefined });
        return "cancelled";
    }
    if (result.kind === "already_terminal") {
        // The run reached a terminal state first — the click lost a race with
        // the last poll. Leave the row alone; the next tick carries the real
        // outcome, and claiming we stopped it would take credit for that.
        return "already_finished";
    }
    if (result.kind === "not_found") {
        // The BE has no record of this run at all, so no outcome is coming and
        // there is nothing left to poll for. Drop the row rather than mark it
        // cancelled: we did not stop anything, and the previous code left it
        // sitting at `running` while `poll()` kept 404ing until MAX_POLL_ERRORS
        // relabelled it "Lost contact / failed" ~15s later — telling an operator
        // who asked for a stop that their scan crashed instead.
        //
        // Removing it also stops the poll for free: the loop halts as soon as
        // its row is no longer in the store.
        removeScanJob(id);
        return "already_finished";
    }
    return "error";
}

/**
 * Re-submit a failed run with its original request. Returns false when the job
 * has no retained `input` (a rehydrated job — see the ScanJob.input note), so
 * the caller can hide Retry rather than send a half-built request.
 */
export async function retryScanJob(id: string): Promise<boolean> {
    const job = jobs.find((j) => j.assessment_id === id);
    if (!job?.input) return false;
    const input = job.input;

    // Start the replacement BEFORE dropping the failed row. Removing it first
    // meant any unsuccessful retry left the user with nothing: no new run, and
    // no row left to inspect or retry from.
    let result: StartResult;
    try {
        result = await startAssessment(input);
    } catch (err) {
        // Auth expiry / network. The caller fires this with `void`, so a
        // rejection here would surface nowhere at all.
        catchErrorLog(`[oura] retry failed for ${id}: ${err}`);
        return false;
    }

    if (!result.ok) {
        // A refusal RESOLVES — startAssessment returns { ok: false } without
        // creating a job — so the happy path has to check this too, not just
        // catch. Only a duplicate counts as success: the backend already has a
        // live run for this domain, so the failed row is genuinely superseded.
        if (result.kind !== "duplicate") return false;
        // That run may have started in another tab, in which case it isn't in
        // the store yet. Seed it, or removing the failed row leaves a gap.
        // (Bound to a const: narrowing on a `let` doesn't survive the callback.)
        const { existing } = result;
        if (!jobs.some((j) => j.assessment_id === existing.assessment_id)) {
            jobs = [existing, ...jobs];
        }
    }

    removeScanJob(id); // emits
    return true;
}

/**
 * Re-seed the store from the BE after a page load, so a scan started before a
 * refresh keeps reporting instead of vanishing. Anything still queued/running
 * resumes polling; recently-settled runs are skipped — the Businesses table
 * already shows those as real rows, and resurrecting them into the toast on
 * every page load would nag about work the user has already seen.
 *
 * Safe to call repeatedly: jobs already in the store are left untouched (their
 * live poll and retained `input` are more complete than the wire payload).
 */
/**
 * Drop every tracked scan and notify subscribers.
 *
 * The store is module-level, so its lifetime is the JS context rather than the
 * session. Today both Oura logout paths do `window.location.href = "/"`, a full
 * navigation that tears the context down and takes this array with it — so the
 * cross-user case cannot currently happen. This exists so it stays that way:
 * the day someone converts logout to a client-side redirect (an ordinary SPA
 * refactor), the store would silently outlive the session it belongs to, and
 * `rehydrateScanJobs()` deliberately MERGES rather than replaces, so nothing
 * downstream would clear it.
 *
 * Each in-flight `poll()` stops on its next tick of its own accord — the loop
 * checks that its row is still in the store — so no timer bookkeeping is needed
 * here.
 */
export function resetScanJobs(): void {
    jobs = [];
    emit();
}

export async function rehydrateScanJobs(): Promise<void> {
    const all = await listAllAssessments();
    const inFlight = all.filter((a) => a.status === "queued" || a.status === "running");
    const fresh = inFlight.filter(
        (a) => !jobs.some((j) => j.assessment_id === a.assessment_id),
    );
    if (fresh.length === 0) return;

    jobs = [
        ...fresh.map((a) => ({
            assessment_id: a.assessment_id,
            domain: a.domain ?? "",
            business_name: a.business_name || a.domain || "Scan in progress",
            category: a.category ?? "",
            status: a.status,
            stage: a.stage ?? "In progress",
            stages_complete: a.stages_complete ?? 0,
            stages_total: a.stages_total ?? 0,
            estimated_cost_usd: 0,
        })),
        ...jobs,
    ];
    emit();
    fresh.forEach((a) => poll(a.assessment_id));
}

function patch(id: string, next: Partial<ScanJob>): void {
    const job = jobs.find((j) => j.assessment_id === id);
    if (!job) return;
    Object.assign(job, next);
    emit();
}

/**
 * Poll one assessment until it reaches a terminal state. `stage` /
 * `stages_complete` are optional on the wire, so each is only applied when the
 * BE actually sent it — never overwrite a real label with `undefined`.
 */
function poll(id: string): void {
    const startedAt = Date.now();
    let errors = 0;

    const tick = window.setInterval(() => {
        // The row was cleared from the store (modal reset, navigation) — stop.
        if (!jobs.some((j) => j.assessment_id === id)) {
            window.clearInterval(tick);
            return;
        }
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
            window.clearInterval(tick);
            // NOT "failed" — we have no idea whether it failed. All we know is
            // that we stopped asking. Reloading re-adopts it from the server if
            // it is still going.
            // `stage` is deliberately NOT overwritten here. It used to become
            // "Still running", which threw away the last thing we knew about a
            // run at the exact moment the user most wants to know it — the row
            // went from "Step 9 of 16: Verifying mentions" to a timeout notice
            // and nothing else. The work that finished did finish; we simply
            // stopped watching.
            patch(id, { status: "untracked", error_code: "POLL_TIMEOUT" });
            return;
        }

        void getAssessment(id)
            .then((a: AeoAssessment) => {
                errors = 0;
                // Drop a response that this browser has already overtaken.
                //
                // `cancelled` and `untracked` are both set here, locally, while a
                // GET fired on an earlier tick may still be in flight. That
                // response was read off the BE BEFORE our action reached it, so
                // it reports "running" and — applied blindly — flips the row back
                // to live after the operator stopped it. `cancelled` self-heals
                // on the next tick; `untracked` does NOT, because its interval is
                // already cleared, so the row reverts permanently to a running
                // state nothing is polling.
                //
                // A TERMINAL wire status is never stale in the way that matters:
                // if the run genuinely completed before the cancel landed, the
                // BE's word is the true one and should win.
                const current = jobs.find((j) => j.assessment_id === id);
                if (
                    current
                    && LOCALLY_SETTLED.includes(current.status)
                    && !TERMINAL.includes(a.status)
                ) {
                    window.clearInterval(tick);
                    return;
                }
                patch(id, {
                    status: a.status,
                    ...(a.stage ? { stage: a.stage } : {}),
                    ...(typeof a.stages_complete === "number"
                        ? { stages_complete: a.stages_complete }
                        : {}),
                    ...(typeof a.stages_total === "number" && a.stages_total > 0
                        ? { stages_total: a.stages_total }
                        : {}),
                    ...(Array.isArray(a.stages) ? { stages: a.stages } : {}),
                    // `eta` is patched even when null — that is the backend
                    // withdrawing an estimate it can no longer stand behind
                    // (the run overran it), and keeping the last one would
                    // leave a stale countdown on screen.
                    ...("eta" in a ? { eta: a.eta ?? null } : {}),
                    ...(a.error_code ? { error_code: a.error_code } : {}),
                });
                if (TERMINAL.includes(a.status)) {
                    window.clearInterval(tick);
                    // Only a run that actually finished says "Done". This was
                    // `!== "failed"`, which relabelled a cancelled run's stage
                    // as "Done" on the poll tick right after the cancel landed —
                    // overwriting the one word telling the user what they did.
                    if (a.status === "complete" || a.status === "partial") {
                        patch(id, { stage: "Done" });
                    }
                }
            })
            .catch(() => {
                // Same staleness rule on the failure path: a request that was
                // overtaken by a local cancel must not count toward marking the
                // run failed. Stopping a scan and being told it crashed is the
                // exact misattribution this whole change exists to avoid.
                const current = jobs.find((j) => j.assessment_id === id);
                if (current && LOCALLY_SETTLED.includes(current.status)) {
                    window.clearInterval(tick);
                    return;
                }
                // A blip shouldn't kill a 15-minute run; a persistent failure
                // (the run vanished, or auth is refusing us) should surface.
                errors += 1;
                if (errors >= MAX_POLL_ERRORS) {
                    window.clearInterval(tick);
                    patch(id, {
                        status: "failed",
                        stage: "Lost contact",
                        error_code: "POLL_FAILED",
                    });
                }
            });
    }, POLL_INTERVAL_MS);
}

export type StartResult =
    | { ok: true; job: ScanJob }
    /** 409 — a run for this domain is already in flight; link, don't double-submit. */
    | { ok: false; kind: "duplicate"; existing: ScanJob }
    | { ok: false; kind: "validation"; errors: string[] }
    | { ok: false; kind: "error"; message: string };

/**
 * POST /api/v1/assessments, then poll it to completion. Resolves as soon as the
 * BE has accepted (or refused) the run — the returned job keeps updating in the
 * store afterwards, which is what the table row renders.
 */
export async function startAssessment(input: NewScanInput): Promise<StartResult> {
    const result = await createAssessment({
        domain: input.domain,
        business_name: input.business_name,
        category: input.category || undefined,
        anchors: { primary: input.anchors.primary, city: input.anchors.city || undefined },
        services: input.services,
        requested_by: input.requested_by?.trim() || undefined,
    });

    if (!result.ok) {
        if (result.kind === "duplicate") {
            // Prefer the row we already have — it carries the labels and live
            // progress. If the in-flight run started in another tab or before a
            // reload we only know its id, so show a minimal placeholder rather
            // than pretending we know its stage.
            const known = jobs.find((j) => j.assessment_id === result.assessment_id)
                ?? jobs.find(
                    (j) => j.domain === input.domain && !TERMINAL.includes(j.status),
                );
            const existing: ScanJob = known ?? {
                assessment_id: result.assessment_id,
                domain: input.domain,
                business_name: input.business_name,
                category: input.category,
                status: "running",
                stage: "In progress",
                stages_complete: 0,
                stages_total: 0,
                estimated_cost_usd: 0,
            };
            return { ok: false, kind: "duplicate", existing };
        }
        return result;
    }

    const { assessment_id, estimated_cost_usd } = result.data;
    const job: ScanJob = {
        assessment_id,
        domain: input.domain,
        business_name: input.business_name,
        category: input.category,
        status: "queued",
        stage: "Queued",
        stages_complete: 0,
        stages_total: 0,
        estimated_cost_usd: estimated_cost_usd ?? 0,
        input,
    };
    jobs = [job, ...jobs];
    emit();
    poll(assessment_id);

    return { ok: true, job };
}
